import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AmplitudeService } from '@app/core/modules/shared/services/analytics/amplitude.service';

/**
 * Page name mapping from route path to human-readable Amplitude page_name.
 * Based on the Event Catalog in the LH-13 spec.
 * All known routes must map to a bounded page name; unmapped routes fall back to 'unknown'.
 */
function resolvePageName(path: string): string {
  if (path === '/auth/login' || path.startsWith('/auth/login')) return 'login';
  if (path === '/auth/verify-otp' || path.startsWith('/auth/verify-otp')) return 'otp_verification';
  if (path === '/authenticated/home' || path.startsWith('/authenticated/home')) return 'home';
  if (path.startsWith('/authenticated/my-properties')) return 'my_properties';
  if (/\/authenticated\/properties\/[^/]+/.test(path)) return 'property_detail';
  if (path.startsWith('/authenticated/available-properties')) return 'available_properties';
  if (path.startsWith('/authenticated/applications/new')) return 'new_application';
  if (/\/authenticated\/applications\/[^/]+\/edit/.test(path)) return 'edit_application';
  if (path.startsWith('/authenticated/applications')) return 'applications_list';
  if (path.startsWith('/authenticated/profile')) return 'profile';
  if (path.startsWith('/public/applications')) return 'public_application_form';
  // Fallback for unmapped routes: bounded to 'unknown' instead of leaking raw URL
  if (!path) return 'unknown';
  console.warn(`[resolvePageName] Unmapped route: ${path}`);
  return 'unknown';
}

@Component({
  selector: 'app-root',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [RouterModule],
  template: ` <router-outlet></router-outlet> `,
})
export class AppComponent implements OnInit {
  private _amplitudeService = inject(AmplitudeService);
  private _router = inject(Router);

  ngOnInit(): void {
    // Wire page-view tracking synchronously so router subscriptions are established
    // before Angular continues bootstrapping. initialize() is async (dynamic import);
    // track() queues events fired before the SDK loads and flushes them once ready,
    // so the first page_viewed event is never lost.
    this._wirePageViewTracking();
    this._amplitudeService.initialize().catch(() => {
      // Analytics SDK failed to load (e.g., network error, ad-blocker) — degrade silently
    });
  }

  /**
   * Wire automatic page_viewed events on every completed navigation.
   *
   * NavigationStart is captured first to record the previous page before
   * the URL changes. NavigationEnd fires the event. All navigations are
   * tracked including guard-triggered redirects — their short dwell times
   * distinguish them from user-initiated navigations in Amplitude session data.
   *
   * Decision: track all NavigationEnd events without trigger filtering (simpler
   * approach noted in the implementation plan). Guard redirects produce events
   * with negligible dwell times and do not skew funnel analysis meaningfully.
   */
  private _wirePageViewTracking(): void {
    let previousPage = '';

    // Capture previous page path on NavigationStart, before the URL changes
    this._router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => {
        previousPage = resolvePageName(this._router.url.split('?')[0].split('#')[0]);
      });

    this._router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const path = e.urlAfterRedirects.split('?')[0].split('#')[0];
        const pageName = resolvePageName(path);
        this._amplitudeService.track('page_viewed', {
          page_name: pageName,
          page_path: path,
          previous_page: previousPage,
        });
        previousPage = pageName;
      });
  }
}
